import { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../data/store';
import {
	fetchValuesThunk,
	updateValueThunk,
} from '../../../../data/thunks/projectDataThunk';
import { fetchProject } from '../../../../data/thunks/projectThunk';
import { fetchClient } from '../../../../data/thunks/clientThunk';
import { useParams } from 'react-router-dom';
import {
	ProjectDataValue,
	ProjectDataOption,
} from '../../../../data/models/projectData';
import Input from '../../../common/form/Input';
import Select from '../../../common/form/Select';
import Slider from '../../../common/form/Slider';
import DatePicker from '../../../common/form/DatePicker';
import RadioButtons from '../../../common/buttons/RadioButtons';
import VerticalTabs from '../../../common/VerticalTabs';
import Paper, { PaperHeader } from '../../../common/layout/Paper';
import { styled } from 'styled-components';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import FullscreenOutlinedIcon from '@mui/icons-material/FullscreenOutlined';
import FullscreenExitOutlinedIcon from '@mui/icons-material/FullscreenExitOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';

interface ProjectInformationProps {
	onToggleFullscreen?: () => void;
	isFullscreen?: boolean;
}

export default function ProjectInformation({
	onToggleFullscreen,
	isFullscreen = false,
}: ProjectInformationProps) {
	const dispatch = useDispatch<AppDispatch>();
	const { projectId } = useParams();

	const { values, pointDefinitions } = useSelector(
		(state: RootState) => state.projectData
	);
	const { project } = useSelector((state: RootState) => state.projectState);

	const [selectedPanel, setSelectedPanel] = useState('');
	const [selectedTabIndex, setSelectedTabIndex] = useState(0);

	useEffect(() => {
		dispatch(fetchValuesThunk({ projectId: projectId }));
		if (projectId) {
			dispatch(fetchProject(projectId));
		}
	}, [dispatch, projectId]);

	// Fetch client data when project is loaded
	useEffect(() => {
		if (project.id && project.client?.id) {
			dispatch(fetchClient(project.client.id));
		}
	}, [dispatch, project.id, project.client?.id]);

	const panels = useMemo(() => {
		const _panels: Panel[] = [];
		const groupedValues = values.reduce(
			(
				acc: {
					[key: string]: { data: ProjectDataValue[]; name: string };
				},
				value
			) => {
				const categoryName = value.projectDataCategoryName;
				if (!acc[categoryName]) {
					acc[categoryName] = {
						data: [],
						name: categoryName,
					};
				}
				acc[categoryName].data.push(value);
				return acc;
			},
			{}
		);

		// Create panels for each category, sorted alphabetically
		Object.entries(groupedValues)
			.sort(([, a], [, b]) => a.name.localeCompare(b.name))
			.forEach(([categoryName, categoryValues]) => {
				_panels.push({
					key: categoryName,
					label: categoryValues.name,
					component: <DataTable values={categoryValues.data} />,
					onclick: () => setSelectedPanel(categoryName),
				});
			});

		if (_panels.length > 0 && !selectedPanel) {
			setSelectedPanel(_panels[0].key);
		}

		return _panels;
	}, [values, selectedPanel]);

	return (
		<PaperContainer gradientBorder $isFullscreen={isFullscreen}>
			<MainContent>
				<HeaderContainer>
					<PaperHeader>Project Information</PaperHeader>
					<ViewToggleContainer>
						<IconButton
							onClick={onToggleFullscreen}
							color={isFullscreen ? 'primary' : 'default'}
						>
							{isFullscreen ? (
								<FullscreenExitOutlinedIcon fontSize="small" />
							) : (
								<FullscreenOutlinedIcon fontSize="small" />
							)}
						</IconButton>
					</ViewToggleContainer>
				</HeaderContainer>
				<TabsContainer className="vertical-tabs">
					<VerticalTabs
						tabs={panels.map((panel) => ({
							label: panel.label,
							content: panel.component,
						}))}
						value={selectedTabIndex}
						setValue={setSelectedTabIndex}
					/>
				</TabsContainer>
			</MainContent>
		</PaperContainer>
	);
}

interface Panel {
	key: string;
	label: string;
	component: React.ReactNode;
	onclick?: () => void;
}

interface DataTableProps {
	values: ProjectDataValue[];
}

// Convert ProjectDataOption[] to Option[] for Select
const mapOptions = (options?: ProjectDataOption[]) => {
	if (!options) return [];
	return options.map((opt) => ({ id: opt.value, display: opt.label }));
};

// Convert ClientContact[] to Option[] for Select
const mapClientContactsToOptions = (contacts: any[]) => {
	if (!contacts || contacts.length === 0) return [];
	return contacts.map((contact) => ({
		id: contact.uuid,
		display: `${contact.firstName} ${contact.lastName}`,
	}));
};

const DataTable = ({ values }: DataTableProps) => {
	const theme = useTheme();
	const containerRef = useRef<HTMLDivElement>(null);
	const [maxBodyHeight, setMaxBodyHeight] = useState('300px');

	useEffect(() => {
		const calculateMaxHeight = () => {
			if (containerRef.current) {
				const containerRect =
					containerRef.current.getBoundingClientRect();
				const windowHeight = window.innerHeight;

				// Calculate space from container top to window bottom
				const availableHeight = windowHeight - containerRect.top;

				// Subtract padding/margins and buffer for proper spacing
				const calculatedHeight = Math.max(availableHeight - 70, 200); // Minimum 200px

				setMaxBodyHeight(`${calculatedHeight}px`);
			}
		};

		calculateMaxHeight();
		window.addEventListener('resize', calculateMaxHeight);

		return () => {
			window.removeEventListener('resize', calculateMaxHeight);
		};
	}, []);

	return (
		<Box
			ref={containerRef}
			sx={{ marginBottom: '16px', marginRight: '10px' }}
		>
			{/* Fixed Header Table */}
			<StyledTableHeader>
				<TableHead>
					<TableRow>
						<TableCell
							sx={{
								fontWeight: 600,
								color: theme.palette.text.secondary,
								borderBottom: 'none',
								paddingBottom: '8px',
								width: '60%',
								maxWidth: '500px',
							}}
						>
							Data Point
						</TableCell>
						<TableCell
							sx={{
								fontWeight: 600,
								color: theme.palette.text.secondary,
								borderBottom: 'none',
								paddingBottom: '8px',
								width: '40%',
							}}
						>
							Value
						</TableCell>
					</TableRow>
				</TableHead>
			</StyledTableHeader>

			{/* Scrollable Body */}
			<Box
				sx={{
					maxHeight: maxBodyHeight,
					overflowY: 'auto',
					'&::-webkit-scrollbar': {
						width: '6px',
					},
					'&::-webkit-scrollbar-track': {
						backgroundColor: 'transparent',
					},
					'&::-webkit-scrollbar-thumb': {
						backgroundColor: 'rgba(0, 0, 0, 0.2)',
						borderRadius: '3px',
						'&:hover': {
							backgroundColor: 'rgba(0, 0, 0, 0.3)',
						},
					},
				}}
			>
				<StyledTableBody>
					<TableBody>
						{values.map((value) => {
							return (
								<TableRow
									key={value.id}
									sx={{
										'&:hover': {
											backgroundColor:
												theme.palette.action.hover,
										},
									}}
								>
									<TableCell
										sx={{
											fontWeight: 500,
											width: '60%',
											maxWidth: '500px',
											wordWrap: 'break-word',
											overflow: 'hidden',
										}}
									>
										{value.projectDataPointDefinition.label}
									</TableCell>
									<TableCell
										sx={{
											width: '40%',
											marginRight: '2px',
										}}
									>
										<TableDataDisplay value={value} />
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</StyledTableBody>
			</Box>
		</Box>
	);
};

const TableDataDisplay = ({ value }: { value: ProjectDataValue }) => {
	const dispatch = useDispatch<AppDispatch>();
	const { client } = useSelector((state: RootState) => state.clientState);
	const theme = useTheme();

	const def = value.projectDataPointDefinition;

	const [dataPointValue, setDataPointValue] = useState(value.value);

	const handleChange = (e: any) => {
		setDataPointValue(e.target.value);
	};

	const handleSubmit = () => {
		if (dataPointValue !== value.value) {
			dispatch(
				updateValueThunk({
					id: value.id,
					value: dataPointValue,
				})
			);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			handleSubmit();
		}
	};

	const handleChangeSelect = (e: any) => {
		const newValue = e.target.value;
		setDataPointValue(newValue);
		dispatch(
			updateValueThunk({
				id: value.id,
				value: newValue,
			})
		);
	};

	const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value;
		setDataPointValue(newValue);
		dispatch(
			updateValueThunk({
				id: value.id,
				value: newValue,
			})
		);
	};

	const handleClearSelection = () => {
		setDataPointValue('');
		dispatch(
			updateValueThunk({
				id: value.id,
				value: '',
			})
		);
	};

	const handleClearSelect = () => {
		setDataPointValue('');
		dispatch(
			updateValueThunk({
				id: value.id,
				value: '',
			})
		);
	};

	const handleDateChange = (date: any) => {
		const dateString = date ? date.format('YYYY-MM-DD') : '';
		setDataPointValue(dateString);
		dispatch(
			updateValueThunk({
				id: value.id,
				value: dateString,
			})
		);
	};

	if (def.type === 'text') {
		return (
			<TableInput
				value={dataPointValue}
				placeholder={'Input text...'}
				onChange={handleChange}
				onBlur={handleSubmit}
				onKeyDown={handleKeyDown}
				variant="outlined"
				size="small"
			/>
		);
	}
	if (def.type === 'select') {
		return (
			<TableSelect
				value={dataPointValue}
				label=""
				options={mapOptions(def.options)}
				onChange={handleChangeSelect}
				clearable={true}
				onClear={handleClearSelect}
				placeholder={'Select option...'}
			/>
		);
	}
	if (def.type === 'boolean') {
		return (
			<RadioButtonContainer>
				<TableRadioButtons
					label=""
					name={`boolean-${value.id}`}
					options={[
						{ value: 'true', label: 'Yes' },
						{ value: 'false', label: 'No' },
					]}
					value={dataPointValue}
					onChange={handleRadioChange}
					row
				/>
				{dataPointValue && (
					<IconButton
						onClick={handleClearSelection}
						size="small"
						sx={{
							marginLeft: '8px',
							padding: '4px',
							color: theme.palette.text.secondary,
							'&:hover': {
								color: theme.palette.error.main,
							},
						}}
						title="Clear selection"
					>
						<ClearIcon fontSize="small" />
					</IconButton>
				)}
			</RadioButtonContainer>
		);
	}
	if (def.type === 'ranking') {
		const handleSliderChange = (
			_event: Event,
			newValue: number | number[]
		) => {
			const sliderValue = Array.isArray(newValue)
				? newValue[0]
				: newValue;

			// Treat 0 as cleared, 1-10 as valid rankings
			const stringValue = sliderValue === 0 ? '' : sliderValue.toString();
			setDataPointValue(stringValue);
			dispatch(
				updateValueThunk({
					id: value.id,
					value: stringValue,
				})
			);
		};

		const handleInputChange = (
			event: React.ChangeEvent<HTMLInputElement>
		) => {
			const inputValue = event.target.value;
			const numericValue = parseInt(inputValue, 10);

			// Validate the input value is within range (1-10) or allow empty/0
			if (
				!isNaN(numericValue) &&
				numericValue >= 1 &&
				numericValue <= 10
			) {
				setDataPointValue(inputValue);
				dispatch(
					updateValueThunk({
						id: value.id,
						value: inputValue,
					})
				);
			} else if (inputValue === '' || inputValue === '0') {
				// Clear the value
				setDataPointValue('');
				dispatch(
					updateValueThunk({
						id: value.id,
						value: '',
					})
				);
			}
		};

		// Convert empty string to 0 for slider display, otherwise parse the number
		const sliderValue =
			dataPointValue === ''
				? 0
				: dataPointValue
				? parseInt(dataPointValue, 10)
				: 0;

		return (
			<TableSlider
				value={sliderValue}
				min={0}
				max={10}
				step={1}
				marks
				onChange={handleSliderChange}
				aria-label="Ranking"
				showInput
				inputValue={dataPointValue}
				onInputChange={handleInputChange}
				clearable={true}
				onClear={handleClearSelect}
			/>
		);
	}
	if (def.type === 'date') {
		return (
			<TableDatePicker
				value={dataPointValue}
				label=""
				onChange={handleDateChange}
			/>
		);
	}
	if (def.type === 'number') {
		return (
			<TableNumberInput
				type="number"
				value={dataPointValue}
				placeholder={'Input value...'}
				onChange={handleChange}
				onBlur={handleSubmit}
				onKeyDown={handleKeyDown}
				variant="outlined"
				size="small"
				inputProps={{ min: 0 }}
			/>
		);
	}
	if (def.type === 'client_contacts') {
		const isMultiple = def.multiple === true;

		// Handle value transformation for multiple vs single selection
		let selectValue: string | string[];
		if (isMultiple) {
			selectValue = Array.isArray(dataPointValue)
				? dataPointValue
				: dataPointValue
				? [dataPointValue]
				: [];
		} else {
			selectValue = dataPointValue || '';
		}

		return (
			<TableSelect
				value={selectValue}
				label=""
				options={mapClientContactsToOptions(client.contacts)}
				onChange={handleChangeSelect}
				multiple={isMultiple}
				clearable={true}
				onClear={handleClearSelect}
				placeholder={'Select contact...'}
			/>
		);
	}
	if (def.type === 'hyperlink') {
		const handleCopyToClipboard = async () => {
			try {
				await navigator.clipboard.writeText(dataPointValue || '');
			} catch (err) {
				console.error('Failed to copy text: ', err);
			}
		};

		return (
			<TableInput
				value={dataPointValue}
				placeholder={'Input hyperlink...'}
				onChange={handleChange}
				onBlur={handleSubmit}
				onKeyDown={handleKeyDown}
				variant="outlined"
				size="small"
				InputProps={{
					endAdornment: (
						<IconButton
							onClick={handleCopyToClipboard}
							size="small"
							sx={{
								color: theme.palette.text.secondary,
								'&:hover': {
									color: theme.palette.primary.main,
								},
							}}
						>
							<ContentCopyIcon fontSize="small" />
						</IconButton>
					),
				}}
			/>
		);
	}
	return <span>{dataPointValue || '-'}</span>;
};

const MainContent = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	height: 100%;
	overflow: visible;
`;

const HeaderContainer = styled.div`
	padding: 10px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-shrink: 0;
`;

const TabsContainer = styled.div`
	padding-left: 16px;
	flex: 1;
	display: flex;
	flex-direction: column;
	height: 100%;
	overflow: visible;

	/* When using vertical tabs, remove left padding and let VerticalTabs handle layout */
	&.vertical-tabs {
		padding-left: 16px;
		overflow: visible;
	}
`;

const PaperContainer = styled(Paper)<{ $isFullscreen?: boolean }>`
	margin-top: ${({ $isFullscreen }) => ($isFullscreen ? '0' : '10px')};
	display: flex;
	flex-direction: column;
	overflow: visible;
	height: ${({ $isFullscreen }) => ($isFullscreen ? '100%' : 'auto')};
	border-radius: ${({ $isFullscreen }) => ($isFullscreen ? '0' : undefined)};
`;

const ViewToggleContainer = styled.div`
	display: flex;
	gap: 4px;
`;

const TableInput = styled(Input)`
	& .MuiInputBase-root {
		font-size: 0.875rem;
	}
	& .MuiInputBase-input {
		padding: 8px 12px;
	}
`;

const TableSelect = styled(Select)`
	& .MuiInputBase-root {
		font-size: 0.875rem;
	}
	& .MuiInputBase-input {
		padding: 8px 12px;
	}
`;

const TableSlider = styled(Slider)`
	& .MuiSlider-root {
		font-size: 0.875rem;
	}
	& .MuiSlider-thumb {
		width: 16px;
		height: 16px;
	}
	& .MuiSlider-rail {
		height: 4px;
	}
	& .MuiSlider-track {
		height: 4px;
	}
`;

const TableDatePicker = styled(DatePicker)`
	& .MuiInputBase-root {
		font-size: 0.875rem;
	}
	& .MuiInputBase-input {
		padding: 8px 12px;
	}
	& input[type='date']::-webkit-calendar-picker-indicator {
		filter: invert(1);
		cursor: pointer;
	}
	& input[type='date']::-webkit-inner-spin-button,
	& input[type='date']::-webkit-outer-spin-button {
		display: none;
		-webkit-appearance: none;
	}
`;

const TableNumberInput = styled(Input)`
	& .MuiInputBase-root {
		font-size: 0.875rem;
	}
	& .MuiInputBase-input {
		padding: 8px 12px;
	}
`;

const StyledTableHeader = styled(Table)`
	& .MuiTableRow-root:last-child .MuiTableCell-root {
		border-bottom: none;
	}
	& .MuiTableHead-root {
		border-bottom: 1px solid ${(props) => props.theme.palette.divider};
	}
	& .MuiTableCell-root {
		padding: 8px 12px;
	}
	background-color: #2f2f2f;
	border-radius: 10px 10px 0 0;
	table-layout: fixed;
	width: 100%;
	padding-right: 2px;
`;

const StyledTableBody = styled(Table)`
	& .MuiTableRow-root:last-child .MuiTableCell-root {
		border-bottom: none;
	}
	& .MuiTableCell-root {
		padding: 8px 12px;
	}
	background-color: #2f2f2f;
	border-radius: 0 0 10px 10px;
	table-layout: fixed;
	width: 100%;
	margin-top: -1px; /* Remove gap between header and body */
`;

const RadioButtonContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const TableRadioButtons = styled(RadioButtons)`
	& .MuiFormControl-root {
		margin: 0;
	}

	& .MuiFormLabel-root {
		font-size: 0.875rem;
		color: ${(props) => props.theme.palette.text.secondary};
		margin-bottom: 4px;
	}

	& .MuiRadio-root {
		padding: 4px;
	}

	& .MuiFormControlLabel-root {
		margin: 0;
		margin-right: 16px;
	}

	& .MuiFormControlLabel-label {
		font-size: 0.875rem;
	}
`;
